import { StopOutlined } from '@ant-design/icons'
import axios from 'axios'
import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import Loader from '../Loader/Loader'
import cn from './Image.module.scss'

export type ImageScope = 'main' | 'all'

export type ProductImage = {
  url: string
  extension: string
  name?: string
  type?: string
  isMain?: boolean
}

type ImageCacheState = {
  status: 'idle' | 'loading' | 'loaded' | 'error'
  images?: ProductImage[]
}

const imageCache: Map<string, ImageCacheState> = new Map()
const activeRequests: Map<string, Promise<ProductImage[] | null>> = new Map()

const IS_PROD = import.meta.env.MODE === 'production'

const cacheKey = (code: number, scope: ImageScope) => `${scope}:${code}`

interface DocFile {
  extension?: string
  ContentType?: string
  dvdan?: string
  type?: string
  name?: string
  isMain?: boolean
}

const getMimeType = (file: DocFile): string => {
  const extensionMap: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    avif: 'image/avif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
  }
  return (
    extensionMap[file.extension?.toLowerCase() as keyof typeof extensionMap] ||
    file.ContentType ||
    'image/jpeg'
  )
}

export function updateImageCache(
  code: number,
  images: ProductImage[],
  scope: ImageScope = 'main'
) {
  imageCache.set(cacheKey(code, scope), { status: 'loaded', images })
}

export function clearImageCache(code: number, scope?: ImageScope) {
  if (scope) {
    imageCache.delete(cacheKey(code, scope))
  } else {
    imageCache.delete(cacheKey(code, 'main'))
    imageCache.delete(cacheKey(code, 'all'))
  }
}

const loadImages = (
  code: number,
  scope: ImageScope = 'main'
): Promise<ProductImage[] | null> => {
  const key = cacheKey(code, scope)

  if (activeRequests.has(key)) return activeRequests.get(key)!

  const cached = imageCache.get(key)
  if (cached?.status === 'loaded' && cached.images)
    return Promise.resolve(cached.images)

  const promise = axios
    .get(`/tunnel/evan/pipss/hs/v1/docs/${code}`, {
      params: { imageScope: scope },
      timeout: 5000,
    })
    .then((res) => {
      const files: DocFile[] = res.data?.file ?? []
      const images: ProductImage[] = files
        .filter((f): f is DocFile & { dvdan: string } => Boolean(f?.dvdan))
        .filter((f) =>
          scope === 'main' ? f.type === 'IMAGE' || !f.type : true
        )
        .filter(
          (f) =>
            f.ContentType?.startsWith('image/') ||
            ['jpg', 'jpeg', 'png', 'webp', 'jfif'].includes(
              (f.extension ?? '').toLowerCase()
            )
        )
        .filter((f) => (f.dvdan.length * 3) / 4 / (1024 * 1024) <= 10)
        .map((f) => ({
          url: `data:${getMimeType(f)};base64,${f.dvdan.replace(/[^A-Za-z0-9+/=]/g, '')}`,
          extension: (f.extension || 'jpg').toLowerCase(),
          name: f.name,
          type: f.type,
          isMain: f.isMain,
        }))
      if (!images.length) throw new Error('No valid images')
      imageCache.set(key, { status: 'loaded', images })
      return images
    })
    .catch((err) => {
      console.error(`Img error ${code}`, err)
      imageCache.set(key, { status: 'error' })
      return null
    })
    .finally(() => activeRequests.delete(key))

  activeRequests.set(key, promise)
  if (!imageCache.has(key)) imageCache.set(key, { status: 'loading' })
  return promise
}

export function useProductImages(
  code: number | null,
  version?: number,
  scope: ImageScope = 'main'
) {
  const [state, setState] = useState<ImageCacheState>(
    code
      ? (imageCache.get(cacheKey(code, scope)) ?? { status: 'idle' })
      : { status: 'idle' }
  )

  useEffect(() => {
    if (!code) {
      setState({ status: 'idle' })
      return
    }

    if (!IS_PROD) {
      setState({ status: 'error' })
      return
    }

    const key = cacheKey(code, scope)
    const cached = imageCache.get(key)
    if (cached?.status === 'loaded' || cached?.status === 'error') {
      setState(cached)
      return
    }

    let mounted = true
    setState(imageCache.get(key) ?? { status: 'loading' })

    loadImages(code, scope).then((images) => {
      if (!mounted) return
      setState(
        imageCache.get(key) ?? {
          status: images ? 'loaded' : 'error',
          images: images ?? undefined,
        }
      )
    })

    return () => {
      mounted = false
    }
  }, [code, version, scope])

  return {
    images: state.images ?? [],
    loading: state.status === 'loading' || state.status === 'idle',
  }
}

function Image({
  productCode,
  className,
  hideOnError,
  onLoadEnd,
  disableLoader,
  imageIndex = 0,
  scope = 'main',
}: {
  productCode: number
  className?: string
  hideOnError?: boolean
  onLoadEnd?: () => void
  disableLoader?: boolean
  imageIndex?: number
  scope?: ImageScope
}) {
  const onLoadEndRef = useRef(onLoadEnd)
  useEffect(() => {
    onLoadEndRef.current = onLoadEnd
  })

  const key = cacheKey(productCode, scope)

  const [state, setState] = useState<ImageCacheState>(
    imageCache.get(key) || { status: 'idle' }
  )

  useEffect(() => {
    const cached = imageCache.get(cacheKey(productCode, scope))
    const newState = cached || { status: 'idle' as const }
    setState(newState)
    if (newState.status === 'loaded' || newState.status === 'error') {
      onLoadEndRef.current?.()
    }
  }, [productCode, scope])

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    rootMargin: '100px 0px',
  })

  useEffect(() => {
    if (!IS_PROD) {
      if (state.status !== 'error') {
        setState({ status: 'error' })
        onLoadEndRef.current?.()
      }
      return
    }

    if (state.status === 'loaded' || state.status === 'error') return

    const isAlreadyLoading = activeRequests.has(key)
    if (!inView && !isAlreadyLoading && state.status !== 'loading') return

    let isMounted = true

    loadImages(productCode, scope).then((images) => {
      if (!isMounted) return
      if (images) {
        setState({ status: 'loaded', images })
      } else {
        setState({ status: 'error' })
      }
      onLoadEndRef.current?.()
    })

    return () => {
      isMounted = false
    }
  }, [productCode, inView, state.status, scope])

  const currentImage = state.images?.[imageIndex]
  const isError =
    state.status === 'error' || (state.status === 'loaded' && !currentImage)

  if (hideOnError && isError) return null

  return (
    <div ref={ref} className={`${cn.imageWrapper} ${className || ''}`}>
      {!disableLoader &&
        (state.status === 'loading' || state.status === 'idle') && (
          <div className={cn.placeholderContent}>
            <Loader size={10} />
          </div>
        )}

      {isError && !hideOnError && (
        <div className={cn.placeholderContent}>
          <StopOutlined className={cn.errorIcon} />
        </div>
      )}

      {state.status === 'loaded' && currentImage && (
        <img
          src={currentImage.url}
          alt="Товар"
          className={cn.image}
          data-product-code={productCode}
        />
      )}
    </div>
  )
}

export default React.memo(Image, (prevProps, nextProps) => {
  return (
    prevProps.productCode === nextProps.productCode &&
    prevProps.imageIndex === nextProps.imageIndex &&
    prevProps.scope === nextProps.scope
  )
})
